import { useState, useEffect, useCallback, useRef } from 'react';
import $ from 'jquery';
import 'bootstrap-datepicker';
import 'bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.css';
import { superAdminApi } from '../services/api';
import { SuperAdminNav } from './SuperAdminConsole';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const AVATAR_COLORS = [
  { bg: 'var(--gr-lt)', fg: '#0A7A4A' },
  { bg: 'var(--bl-lt)', fg: '#1D4ED8' },
  { bg: 'var(--or-lt)', fg: 'var(--or-dk)' },
  { bg: 'var(--pu-lt)', fg: '#5B21B6' },
  { bg: 'var(--rd-lt)', fg: '#C53030' },
  { bg: 'var(--g100)', fg: 'var(--g600)' },
];

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

function toIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// "2026-08-20" -> "20-Aug-2026" (API expects dd-MMM-yyyy)
function isoToApi(iso) {
  if (!iso) return '';
  const [yyyy, mm, dd] = iso.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

// "2026-08-20" -> "20-Aug-2026"
function fmtDisplay(iso) {
  if (!iso) return '';
  const [yyyy, mm, dd] = iso.split('-');
  return `${dd}-${MONTHS[parseInt(mm, 10) - 1]}-${yyyy}`;
}

function rangeForPreset(preset) {
  const end = new Date();
  let start = new Date();
  if (preset === 'today') { /* start = end */ }
  else if (preset === '7') start.setDate(start.getDate() - 6);
  else if (preset === '30') start.setDate(start.getDate() - 29);
  else if (preset === '90') start.setDate(start.getDate() - 89);
  else if (preset === 'month') start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { from: toIso(start), to: toIso(end) };
}

function inr(n) { return '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN'); }

function initials(name) {
  const w = (name || '').replace(/\(.*?\)/g, '').trim().split(/\s+/);
  return ((w[0] || '')[0] + ((w[1] || '')[0] || '')).toUpperCase() || '?';
}

function colorFor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Text input showing dd-MMM-yyyy with a bootstrap-datepicker calendar (value/onChange are ISO yyyy-mm-dd)
function DateField({ value, onChange, placeholder = 'dd-MMM-yyyy', max, min }) {
  const ref = useRef(null);

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker({
      format: 'dd-M-yyyy',
      autoclose: true,
      todayHighlight: true,
      orientation: 'bottom auto',
      endDate: max ? new Date(`${max}T00:00:00`) : undefined,
      startDate: min ? new Date(`${min}T00:00:00`) : undefined,
    });
    $input.on('changeDate', e => onChange(toIso(e.date)));
    return () => { $input.datepicker('destroy'); $input.off('changeDate'); };
  }, []); // eslint-disable-line

  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker('setEndDate', max ? new Date(`${max}T00:00:00`) : null);
    $input.datepicker('setStartDate', min ? new Date(`${min}T00:00:00`) : null);
  }, [max, min]);

  // Keep the picker in sync when `value` changes externally (e.g. a preset pill click)
  useEffect(() => {
    const $input = $(ref.current);
    $input.datepicker('update', value ? new Date(`${value}T00:00:00`) : '');
  }, [value]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <input
        ref={ref}
        className="inp"
        type="text"
        readOnly
        defaultValue={fmtDisplay(value)}
        placeholder={placeholder}
        style={{ cursor: 'pointer', paddingRight: 28, width: 150 }}
      />
      <span style={{ position: 'absolute', right: 8, cursor: 'pointer', color: 'var(--g400)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>
    </div>
  );
}

function ClientList({ items, positive }) {
  if (!items || items.length === 0) {
    return <div className="wempty">No transactions in this period</div>;
  }
  return items.map((it, i) => {
    const name = it.client_name || it.clientname || '—';
    const amount = it.amount ?? 0;
    const c = colorFor(name);
    return (
      <div className="wrow" key={it.client_id ?? i}>
        <div className="wavt" style={{ background: c.bg, color: c.fg }}>{initials(name)}</div>
        <div className="wname">{name}</div>
        <div className="wamt" style={{ color: positive ? 'var(--gr)' : 'var(--rd)' }}>
          {positive ? '+' : '-'}{inr(amount)}
        </div>
      </div>
    );
  });
}

// Splits [fromISO, toISO] into day / month / year buckets depending on range length,
// mirroring the granularity switch used by the dashboard's day-wise credit chart.
function buildDayBuckets(fromISO, toISO) {
  const start = new Date(`${fromISO}T00:00:00`);
  const end = new Date(`${toISO}T00:00:00`);
  if (!(start <= end)) return { granularity: 'day', buckets: [] };
  const diffDays = Math.round((end - start) / 86400000) + 1;
  const granularity = diffDays <= 31 ? 'day' : (diffDays <= 366 ? 'month' : 'year');
  const buckets = [];

  if (granularity === 'day') {
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = toIso(d);
      buckets.push({ label: `${d.getDate()} ${MONTHS[d.getMonth()]}`, from: iso, to: iso });
    }
  } else if (granularity === 'month') {
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const bFrom = cur < start ? start : cur;
      const bTo = monthEnd > end ? end : monthEnd;
      buckets.push({ label: `${MONTHS[cur.getMonth()]} ${cur.getFullYear()}`, from: toIso(bFrom), to: toIso(bTo) });
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
  } else {
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
      const yStart = new Date(y, 0, 1);
      const yEnd = new Date(y, 11, 31);
      const bFrom = yStart < start ? start : yStart;
      const bTo = yEnd > end ? end : yEnd;
      buckets.push({ label: String(y), from: toIso(bFrom), to: toIso(bTo) });
    }
  }
  return { granularity, buckets };
}

const CHART_W = 900, CHART_H = 260;
const CHART_PAD = { l: 54, r: 16, t: 18, b: 32 };

// Day-wise Credit Summary: bars + line both trace the credited amount per bucket.
// `days` / `dayClientCredits` are the sparse per-day rows the API returns (only days with a
// transaction); buckets for the full range are built here and zero-filled where there's no data.
function DaywiseChart({ from, to, days, dayClientCredits, loading }) {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null); // { x, y, bucket }

  const dayMap = {};
  (days || []).forEach(d => {
    const iso = String(d.txn_date || '').slice(0, 10);
    dayMap[iso] = { credited: Number(d.credited_amount) || 0, spent: Number(d.spent_amount) || 0 };
  });
  const clientMap = {};
  (dayClientCredits || []).forEach(c => {
    const iso = String(c.txn_date || '').slice(0, 10);
    (clientMap[iso] = clientMap[iso] || []).push({ name: c.client_name || '—', amount: Number(c.credited_amount) || 0 });
  });

  const rawBuckets = (from && to ? buildDayBuckets(from, to) : { buckets: [] }).buckets;

  const buckets = rawBuckets.map(b => {
    let credited = 0, spent = 0;
    const clientTotals = {};
    Object.keys(dayMap).forEach(iso => {
      if (iso < b.from || iso > b.to) return;
      credited += dayMap[iso].credited;
      spent += dayMap[iso].spent;
    });
    Object.keys(clientMap).forEach(iso => {
      if (iso < b.from || iso > b.to) return;
      clientMap[iso].forEach(c => { clientTotals[c.name] = (clientTotals[c.name] || 0) + c.amount; });
    });
    return { ...b, credited, spent, clientTotals };
  });

  const n = buckets.length;
  const { l: padL, r: padR, t: padT, b: padB } = CHART_PAD;
  const plotW = CHART_W - padL - padR, plotH = CHART_H - padT - padB;
  const maxCredited = Math.max(1, ...buckets.map(b => b.credited));
  const slot = n > 0 ? plotW / n : plotW;
  const barW = Math.max(3, Math.min(30, slot * 0.5));
  const labelStep = n > 14 ? Math.ceil(n / 12) : 1;

  const bars = buckets.map((b, i) => {
    const barH = maxCredited > 0 ? Math.max((b.credited / maxCredited) * plotH, 1.5) : 1.5;
    const x = padL + i * slot + (slot - barW) / 2;
    const y = padT + plotH - barH;
    return { ...b, x, y, barH, lineX: x + barW / 2, lineY: y };
  });
  const linePath = bars.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.lineX.toFixed(1)},${p.lineY.toFixed(1)}`).join(' ');
  const gridLines = [0, 1, 2, 3, 4].map(g => {
    const val = maxCredited * g / 4;
    return { gy: padT + plotH - (plotH * g / 4), label: val >= 1000 ? `₹${Math.round(val / 1000)}k` : `₹${Math.round(val)}` };
  });

  function showTip(e, bucket) {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, bucket });
  }
  function moveTip(e) {
    if (!tip) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setTip(t => (t ? { ...t, x: e.clientX - rect.left, y: e.clientY - rect.top } : t));
  }

  const clientRows = tip ? Object.entries(tip.bucket.clientTotals).sort((a, b) => b[1] - a[1]) : [];
  const tipLeft = tip ? (tip.x + 180 > (wrapRef.current?.clientWidth || 900) ? tip.x - 196 : tip.x + 16) : 0;
  const tipTop = tip ? Math.max(4, tip.y - 12) : 0;

  return (
    <div className="chart-svg-wrap" ref={wrapRef}>
      {loading ? (
        <div className="wempty">Loading…</div>
      ) : n === 0 ? (
        <div className="wempty">No data for this period</div>
      ) : (
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} width="100%" height={CHART_H} style={{ overflow: 'visible', display: 'block' }}>
          {gridLines.map((g, i) => (
            <g key={i}>
              <line x1={padL} x2={padL + plotW} y1={g.gy} y2={g.gy} stroke="var(--g100)" strokeWidth="1" />
              <text x={padL - 8} y={g.gy + 3} textAnchor="end" fontSize="9.5" fill="var(--g400)">{g.label}</text>
            </g>
          ))}
          {bars.map((b, i) => (
            <g
              key={i}
              onMouseEnter={e => showTip(e, b)}
              onMouseMove={moveTip}
              onMouseLeave={() => setTip(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect x={padL + i * slot} y={padT} width={slot} height={plotH} fill="transparent" />
              <rect
                x={b.x} y={b.y} width={barW} height={b.barH} rx="3"
                fill={tip?.bucket === b ? (b.credited > 0 ? '#0A7A4A' : 'var(--g300)') : (b.credited > 0 ? 'var(--gr)' : 'var(--g200)')}
              />
              {i % labelStep === 0 && (
                <text x={b.x + barW / 2} y={CHART_H - 10} textAnchor="middle" fontSize="9.5" fill="var(--g400)">{b.label}</text>
              )}
            </g>
          ))}
          <path d={linePath} fill="none" stroke="var(--or)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {bars.map((p, i) => (
            <circle key={i} cx={p.lineX} cy={p.lineY} r="3" fill="var(--or)" stroke="#fff" strokeWidth="1.5" style={{ pointerEvents: 'none' }} />
          ))}
        </svg>
      )}
      {tip && (
        <div className="chart-tooltip" style={{ display: 'block', left: tipLeft, top: tipTop }}>
          <div className="ct-date">{tip.bucket.label}</div>
          <div className="ct-row"><span>Credited</span><span style={{ color: '#3DDC97', fontWeight: 700 }}>{inr(tip.bucket.credited)}</span></div>
          <div className="ct-divider" />
          {clientRows.length ? clientRows.map(([name, amt]) => (
            <div className="ct-client" key={name}><span>{name}</span><span>{inr(amt)}</span></div>
          )) : <div className="ct-empty">No credits in this period</div>}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard({ onNavigate }) {
  const [preset, setPreset] = useState('30');
  const [range, setRange] = useState(() => rangeForPreset('30'));
  const [fromInput, setFromInput] = useState(range.from);
  const [toInput, setToInput] = useState(range.to);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDashboard = useCallback((from, to) => {
    setLoading(true);
    superAdminApi.getWalletDashboard(isoToApi(from), isoToApi(to), 6)
      .then(res => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDashboard(range.from, range.to); }, []); // eslint-disable-line

  function selectPreset(key) {
    setPreset(key);
    if (key === 'custom') return;
    const r = rangeForPreset(key);
    setRange(r);
    setFromInput(r.from);
    setToInput(r.to);
    fetchDashboard(r.from, r.to);
  }

  function applyCustom() {
    if (!fromInput || !toInput) return;
    setPreset('custom');
    setRange({ from: fromInput, to: toInput });
    fetchDashboard(fromInput, toInput);
  }

  const summary = data?.summary || {};
  const totalCredited = summary.total_credited || 0;
  const totalSpent = summary.total_spent || 0;
  const totalBalance = summary.total_balance ?? (totalCredited - totalSpent);
  const creditedCount = summary.clients_credited_count || 0;
  const spentCount = summary.clients_spent_count || 0;
  const periodLabel = `${fmtDisplay(range.from)} – ${fmtDisplay(range.to)}`;

  const daywise = data?.daywisesummary || {};
  const dayGranularity = buildDayBuckets(range.from, range.to).granularity;

  return (
    <>
      <SuperAdminNav onNavigate={onNavigate} onChangePassword={() => {}} currentPage="admin-dashboard" />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, Admin 👋</div>
            <div className="psub">SwiftCheq BGV Command Centre</div>
          </div>
        </div>

        {/* Date range filter */}
        <div className="card mb20">
          <div className="dfilter">
            <div className="drow">
              <div className="flbl">📅 Date range</div>
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`dpill ${preset === p.key ? 'on' : ''}`}
                  onClick={() => selectPreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
              <DateField
                value={fromInput}
                max={toInput}
                onChange={v => { setFromInput(v); setPreset('custom'); }}
              />
              <span className="ddash">to</span>
              <DateField
                value={toInput}
                min={fromInput}
                onChange={v => { setToInput(v); setPreset('custom'); }}
              />
              <button className="btn bpri bsm" onClick={applyCustom}>Apply</button>
            </div>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="csub mb8" style={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.5px' }}>
          Wallet &amp; Credits — {periodLabel}
        </div>
        <div className="gr3 mb20">
          <div className="sc">
            <div className="si si-gr">▲</div>
            <div>
              <div className="slbl">Total Credited</div>
              <div className="sval" style={{ color: 'var(--gr)' }}>{loading ? '…' : inr(totalCredited)}</div>
              <div className="snote">{creditedCount} client{creditedCount !== 1 ? 's' : ''} added credits in period</div>
            </div>
          </div>
          <div className="sc">
            <div className="si si-rd">▼</div>
            <div>
              <div className="slbl">Total Spent</div>
              <div className="sval" style={{ color: 'var(--rd)' }}>{loading ? '…' : inr(totalSpent)}</div>
              <div className="snote">{spentCount} client{spentCount !== 1 ? 's' : ''} placed orders in period</div>
            </div>
          </div>
          <div className="sc">
            <div className="si si-or">₹</div>
            <div>
              <div className="slbl">Total Balance</div>
              <div className="sval" style={{ color: totalBalance < 0 ? 'var(--rd)' : 'var(--gr)' }}>
                {loading ? '…' : (totalBalance < 0 ? '-' : '') + inr(Math.abs(totalBalance))}
              </div>
              <div className="snote">Net credited minus spent, this period</div>
            </div>
          </div>
        </div>

        {/* Day-wise credit summary chart */}
        <div className="card mb20">
          <div className="chd">
            <div>
              <div className="ctitle">📈 Day-wise Credit Summary</div>
              <div className="csub">Grouped by {dayGranularity} · {periodLabel}</div>
            </div>
            <div className="chart-legend">
              <span className="chart-lg-item"><span className="chart-lg-dot" style={{ background: 'var(--gr)' }} />Credited</span>
              <span className="chart-lg-item"><span className="chart-lg-line" style={{ background: 'var(--or)' }} />Credited Trend</span>
            </div>
          </div>
          <DaywiseChart
            from={range.from}
            to={range.to}
            loading={loading}
            days={daywise.days}
            dayClientCredits={daywise.dayclientcredits}
          />
        </div>

        {/* Client-wise credit & spent */}
        <div className="gr2">
          <div className="card">
            <div className="chd">
              <div><div className="ctitle">💰 Recent Credits Added</div><div className="csub">Client-wise, {periodLabel}</div></div>
              <span className="chip cg">Live</span>
            </div>
            {loading ? <div className="wempty">Loading…</div> : <ClientList items={data?.recentcreditsadded} positive />}
          </div>
          <div className="card">
            <div className="chd">
              <div><div className="ctitle">💸 Credits Spent</div><div className="csub">Client-wise, {periodLabel}</div></div>
              <span className="chip cr">Live</span>
            </div>
            {loading ? <div className="wempty">Loading…</div> : <ClientList items={data?.creditsspent} />}
          </div>
        </div>

        {/* New vs Recurring client payments */}
        <div className="card mt20">
          <div className="chd">
            <div><div className="ctitle">🆕 New vs Recurring Client Payments</div><div className="csub">Client-wise, {periodLabel}</div></div>
            <span className="chip cb">Live</span>
          </div>
          <div className="gr2">
            <div>
              <div className="csub mb8" style={{ fontWeight: 700 }}>New Client — First Payment</div>
              {loading ? <div className="wempty">Loading…</div> : <ClientList items={data?.newclientpayments} positive />}
            </div>
            <div>
              <div className="csub mb8" style={{ fontWeight: 700 }}>Recurring Payment Client</div>
              {loading ? <div className="wempty">Loading…</div> : <ClientList items={data?.recurringclientpayments} positive />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
